import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { cn } from "@/lib/utils";

interface Article {
  id: string;
  title: string;
  slug: string;
  image: string;
  category: string;
  reason: string;
}

interface ChatBubbleProps {
  role: "user" | "assistant";
  content: string;
  articles?: Article[];
}

const ChatBubble = ({ role, content, articles }: ChatBubbleProps) => {
  const isUser = role === "user";

  return (
    <div className={cn("flex w-full mb-4", isUser ? "justify-end" : "justify-start")}>
      <div className={cn("max-w-[85%] space-y-3", isUser && "items-end")}>
        {/* Message Bubble */}
        <div
          className={cn(
            "rounded-2xl px-4 py-3 shadow-sm",
            isUser
              ? "bg-primary text-primary-foreground rounded-br-sm"
              : "bg-muted text-foreground rounded-bl-sm"
          )}
        >
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{content}</p>
        </div>

        {/* Recommended Articles */}
        {articles && articles.length > 0 && (
          <div className="space-y-2">
            {articles.map((article) => (
              <Link
                key={article.id}
                to={`/blog/${article.slug}`}
                className="block"
              >
                <Card className="group cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg bg-card border-border overflow-hidden">
                  <div className="flex gap-3 p-3">
                    {/* Thumbnail */}
                    <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded-lg">
                      <AspectRatio ratio={1}>
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </AspectRatio>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="mb-1">
                        <span className="text-xs font-semibold text-primary uppercase tracking-wide">
                          {article.category}
                        </span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground line-clamp-2 mb-1">
                        {article.title}
                      </h3>
                      {article.reason && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          💡 {article.reason}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatBubble;
